import React, { useContext, useRef, useEffect, useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import {
  Box, Container, Typography, Paper, Stack,
  CircularProgress, Button, Stepper, Step, StepLabel, Chip,
  TextField, IconButton, InputAdornment,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import RedeemIcon from "@mui/icons-material/Redeem";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "../../components/Header";
import { myTicket, addTicketFollowUp, chooseRefundOption } from "../../apollo/server";
import UserContext from "../../context/User";

const MY_TICKET = gql`${myTicket}`;
const ADD_FOLLOW_UP = gql`${addTicketFollowUp}`;
const CHOOSE_REFUND = gql`${chooseRefundOption}`;

const money = (v) => `$${(parseFloat(v) || 0).toFixed(2)}`;

// Where credit can be spent, in the customer's words.
function creditWhere(refund) {
  return refund?.creditScope === "RESTAURANT"
    ? `store credit at ${refund.creditRestaurantName || "this restaurant"}`
    : "RMF Wallet credit";
}

/**
 * An approved refund waiting for the customer: back to how they paid, or
 * credit (with any bonus). Credit is store credit when the restaurant was at
 * fault, usable anywhere otherwise — the backend decides, this just shows it.
 */
function RefundChoiceCard({ ticketId, refund }) {
  const [choice, setChoice] = useState(null);
  const [error, setError] = useState("");
  const [choose, { loading }] = useMutation(CHOOSE_REFUND, {
    refetchQueries: [{ query: MY_TICKET, variables: { ticketId } }],
    awaitRefetchQueries: true,
  });

  const confirm = async () => {
    setError("");
    try {
      await choose({ variables: { ticketId, choice } });
    } catch (e) {
      setError(e.message.replace(/^GraphQL error: /, ""));
    }
  };

  const bonus = parseFloat(refund.bonusAmount) || 0;
  const options = [
    {
      key: "ORIGINAL",
      Icon: CreditCardIcon,
      title: "Original payment",
      amount: money(refund.approvedAmount),
      lines: [
        refund.walletRefundAmount > 0 && `${money(refund.walletRefundAmount)} back to your RMF Wallet`,
        refund.cardRefundAmount > 0 && `${money(refund.cardRefundAmount)} back to ${refund.cardLabel || "your card"}`,
        refund.cardRefundAmount > 0 && `Cards take ${refund.cardRefundDays || "3–8 business days"}`,
      ].filter(Boolean),
    },
    {
      key: "CREDIT",
      Icon: RedeemIcon,
      title: "RushMyFood credit",
      amount: money(refund.creditTotal),
      badge: bonus > 0 ? `+${money(bonus)} bonus` : null,
      lines: [
        refund.creditScope === "RESTAURANT"
          ? `Store credit at ${refund.creditRestaurantName || "this restaurant"}`
          : "Use it at any restaurant",
        "Available right away",
      ],
    },
  ];

  return (
    <Paper sx={{ p: 3, borderRadius: "16px", border: "2px solid #FF3008" }}>
      <Typography fontWeight={800} fontSize="1.05rem" color="#111827">
        Your {money(refund.approvedAmount)} refund is approved
      </Typography>
      <Typography fontSize="0.85rem" color="text.secondary" mt={0.4} mb={2}>
        How would you like to get it?
      </Typography>
      <Stack spacing={1.5}>
        {options.map(({ key, Icon, title, amount, badge, lines }) => {
          const active = choice === key;
          return (
            <Box
              key={key}
              role="radio"
              aria-checked={active}
              tabIndex={0}
              onClick={() => setChoice(key)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setChoice(key); } }}
              sx={{
                p: 2, borderRadius: "12px", cursor: "pointer",
                border: active ? "2px solid #FF3008" : "1px solid #e5e7eb",
                bgcolor: active ? "#fff5f2" : "#fff",
                transition: "all 0.15s ease",
                "&:hover": { borderColor: "#FF3008" },
                "&:focus-visible": { outline: "2px solid #FF3008", outlineOffset: 2 },
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                <Icon sx={{ color: active ? "#FF3008" : "#64748b", fontSize: 26, mt: 0.2 }} />
                <Box flex={1} minWidth={0}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Typography fontWeight={700} fontSize="0.95rem" color="#111827">{title}</Typography>
                    <Typography fontWeight={800} fontSize="1rem" color="#111827">{amount}</Typography>
                  </Stack>
                  {badge && (
                    <Chip label={badge} size="small"
                      sx={{ mt: 0.5, height: 20, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#dcfce7", color: "#15803d" }} />
                  )}
                  {lines.map((l) => (
                    <Typography key={l} fontSize="0.8rem" color="text.secondary" mt={0.4}>{l}</Typography>
                  ))}
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Stack>
      {error && <Typography fontSize="0.8rem" color="error.main" mt={1.5}>{error}</Typography>}
      <Button
        fullWidth variant="contained" disabled={!choice || loading} onClick={confirm}
        sx={{ mt: 2, py: 1.2, borderRadius: "10px", fontWeight: 700, textTransform: "none", bgcolor: "#FF3008", "&:hover": { bgcolor: "#e02b00" } }}
      >
        {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Confirm"}
      </Button>
    </Paper>
  );
}

const BACKEND_URL = (process.env.REACT_APP_SERVER_URL || "").replace(/\/$/, "");

const ISSUE_LABELS = {
  MISSING_ITEM: "Missing Item", INCORRECT_ITEM: "Incorrect Item",
  WRONG_ORDER: "Wrong Order", FOOD_QUALITY: "Food Quality",
  NEVER_ARRIVED: "Never Arrived", WRONG_ADDRESS_DROP: "Wrong Drop Address",
  DRIVER_ISSUE: "Driver Issue", ORDER_LATE: "Order Late",
  TECH_BILLING: "Tech/Billing", OTHER: "Other",
};

function fmt(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const SENDER_CONFIG = {
  ADMIN:      { color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe", Icon: AdminPanelSettingsOutlinedIcon },
  RESTAURANT: { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0", Icon: StorefrontOutlinedIcon },
  CUSTOMER:   { color: "#7c3aed", bg: "#faf5ff", border: "#e9d5ff", Icon: PersonOutlineIcon },
};

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useContext(UserContext);
  const threadEndRef = useRef(null);

  // Reset scroll on mount — coming from the list page the scrollY carries
  // over and lands the user in the middle of the conversation.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [id]);

  const [replyText, setReplyText] = useState("");
  const [replyImages, setReplyImages] = useState([]); // [{preview, url}]
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [addFollowUp, { loading: sending }] = useMutation(ADD_FOLLOW_UP, {
    refetchQueries: [{ query: MY_TICKET, variables: { ticketId: id } }],
  });

  const handleImageAttach = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const results = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("image", file);
      try {
        const res = await fetch(`${BACKEND_URL}/upload/image`, { method: "POST", body: fd });
        const json = await res.json();
        if (json.url) results.push({ preview: URL.createObjectURL(file), url: json.url });
      } catch (err) { /* skip failed */ }
    }
    setReplyImages((prev) => [...prev, ...results]);
    setUploading(false);
    e.target.value = "";
  };

  const handleSendReply = async () => {
    const text = replyText.trim();
    if (!text && replyImages.length === 0) return;
    try {
      await addFollowUp({
        variables: {
          ticketId: id,
          description: text || undefined,
          evidenceUrls: replyImages.length > 0 ? replyImages.map((r) => r.url) : undefined,
        },
      });
      setReplyText("");
      setReplyImages([]);
    } catch (err) {
      console.error("Send reply failed:", err);
    }
  };

  const { data, loading } = useQuery(MY_TICKET, {
    variables: { ticketId: id },
    skip: !isLoggedIn || !id,
    fetchPolicy: "cache-and-network",
    pollInterval: 10000,
  });

  const ticket = data?.myTicket;
  const refund = ticket?.refundRequest;
  const isResolved = ticket?.status === "RESOLVED";
  const isDenied = ticket?.status === "CLOSED_NO_ACTION";
  const awaitingChoice = refund?.status === "AWAITING_CUSTOMER";
  const messages = ticket?.messages || [];

  // Auto-scroll to the latest message ONLY when a new one arrives after the
  // initial load. On first paint we leave the user at the top of the page.
  const lastMessageCount = useRef(0);
  useEffect(() => {
    if (messages.length > lastMessageCount.current && lastMessageCount.current > 0) {
      threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    lastMessageCount.current = messages.length;
  }, [messages.length]);

  // Step index: 0=submitted, 1=under review, 2=resolved (or choose refund)
  const stepIndex = isResolved || isDenied || awaitingChoice ? 2 : 1;

  return (
    <>
      <Header />
      <Box sx={{ bgcolor: "#f8fafc", minHeight: "80vh", pt: "88px", pb: 5 }}>
        <Container maxWidth="sm">
          <Button onClick={() => navigate("/my-tickets")} size="small"
            sx={{ mb: 2, color: "text.secondary", textTransform: "none" }}>
            ← Back to My Tickets
          </Button>

          {loading && <Box textAlign="center" py={8}><CircularProgress /></Box>}

          {!loading && !ticket && (
            <Paper variant="outlined" sx={{ p: 5, borderRadius: "16px", textAlign: "center" }}>
              <Typography color="text.secondary">Ticket not found.</Typography>
            </Paper>
          )}

          {ticket && (
            <Stack spacing={2.5}>
              {/* Header card */}
              <Paper sx={{ p: 3, borderRadius: "16px" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography fontWeight={800} fontSize="1.1rem" color="primary.main">
                      {ticket.ticketNumber}
                    </Typography>
                    <Typography fontSize="0.85rem" color="text.secondary" mt={0.3}>
                      Order #{ticket.orderDisplayId || ticket.orderId} · {ISSUE_LABELS[ticket.issueType] || ticket.issueType}
                    </Typography>
                    <Typography fontSize="0.78rem" color="text.secondary" mt={0.2}>
                      Submitted {fmt(ticket.createdAt)}
                    </Typography>
                  </Box>
                  <SupportAgentIcon sx={{ color: "#1d4ed8", fontSize: 32, opacity: 0.7 }} />
                </Stack>
              </Paper>

              {awaitingChoice && <RefundChoiceCard ticketId={id} refund={refund} />}

              {/* YOUR REPORT */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: "14px" }}>
                <Typography fontWeight={700} fontSize="0.78rem" color="text.secondary" mb={1.5} letterSpacing={0.8}>
                  YOUR REPORT
                </Typography>
                <Typography fontSize="0.85rem" mb={0.5}>
                  <strong>Issue:</strong> {ISSUE_LABELS[ticket.issueType] || ticket.issueType}
                </Typography>
                {ticket.description && (
                  <Typography fontSize="0.85rem" mb={0.5}>
                    <strong>Description:</strong> {ticket.description}
                  </Typography>
                )}
                {refund && (
                  <Typography fontSize="0.85rem">
                    <strong>Requested refund:</strong> ${parseFloat(refund.requestedAmount).toFixed(2)}
                  </Typography>
                )}
              </Paper>

              {/* AFFECTED ITEMS */}
              {(() => {
                let items = null;
                try {
                  const raw = refund?.affectedItems;
                  items = raw ? (typeof raw === "string" ? JSON.parse(raw) : raw) : null;
                } catch (e) {}
                if (!items || items.length === 0) return null;
                return (
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: "14px", borderColor: "#fbbf24", bgcolor: "#fffbeb" }}>
                    <Typography fontWeight={700} fontSize="0.78rem" color="#b45309" mb={1.5} letterSpacing={0.8}>
                      REPORTED ITEMS
                    </Typography>
                    <Stack spacing={1.5}>
                      {items.map((item, i) => (
                        <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography fontSize="0.88rem" fontWeight={600}>{item.name}</Typography>
                            <Typography fontSize="0.78rem" color="text.secondary">
                              Ordered: {item.qty} · Reported missing: {item.refundQty}
                            </Typography>
                          </Box>
                          <Typography fontSize="0.88rem" fontWeight={700} color="#b45309">
                            ${parseFloat(item.refundAmount).toFixed(2)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                );
              })()}

              {/* STATUS STEPPER */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: "14px" }}>
                <Typography fontWeight={700} fontSize="0.78rem" color="text.secondary" mb={2} letterSpacing={0.8}>
                  STATUS
                </Typography>
                <Stepper activeStep={stepIndex} alternativeLabel>
                  {["Submitted", "Under Review", isResolved ? "Resolved" : isDenied ? "Closed" : awaitingChoice ? "Choose refund" : "Pending"].map((label, idx) => (
                    <Step key={label} completed={idx < stepIndex}>
                      <StepLabel
                        StepIconComponent={({ active, completed }) =>
                          completed || active
                            ? <CheckCircleIcon sx={{ color: isResolved ? "#16a34a" : isDenied ? "#64748b" : "#1d4ed8", fontSize: 22 }} />
                            : <RadioButtonUncheckedIcon sx={{ color: "#cbd5e1", fontSize: 22 }} />
                        }
                      >
                        <Typography fontSize="0.75rem">{label}</Typography>
                        {idx === 0 && <Typography fontSize="0.7rem" color="text.secondary">{fmt(ticket.createdAt)}</Typography>}
                        {idx === 2 && ticket.resolvedAt && <Typography fontSize="0.7rem" color="text.secondary">{fmt(ticket.resolvedAt)}</Typography>}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Paper>

              {/* MESSAGE THREAD */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: "14px" }}>
                  <Typography fontWeight={700} fontSize="0.78rem" color="text.secondary" mb={2} letterSpacing={0.8}>
                    CONVERSATION
                  </Typography>

                  {/* Initial report bubble — always shown */}
                  {(() => {
                    let items = null;
                    try {
                      const raw = refund?.affectedItems;
                      items = raw ? (typeof raw === "string" ? JSON.parse(raw) : raw) : null;
                    } catch (e) {}
                    return (
                      <Box sx={{ mb: 2, p: 1.5, borderRadius: "10px", bgcolor: "#faf5ff", border: "1px solid #e9d5ff" }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                          <PersonOutlineIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
                          <Typography fontSize="0.72rem" fontWeight={700} color="#7c3aed">You · Initial report</Typography>
                          <Typography fontSize="0.7rem" color="text.secondary">{fmt(ticket.createdAt)}</Typography>
                        </Stack>
                        <Typography fontSize="0.85rem" mb={0.4}>
                          <strong>Issue:</strong> {ISSUE_LABELS[ticket.issueType] || ticket.issueType}
                        </Typography>
                        {ticket.description && (
                          <Typography fontSize="0.85rem" mb={0.4}>
                            <strong>Description:</strong> {ticket.description}
                          </Typography>
                        )}
                        {refund && (
                          <Typography fontSize="0.85rem" mb={items?.length > 0 ? 0.4 : 0}>
                            <strong>Requested refund:</strong> ${parseFloat(refund.requestedAmount).toFixed(2)}
                          </Typography>
                        )}
                        {items?.length > 0 && (
                          <Box mt={0.75} pt={0.75} sx={{ borderTop: "1px solid #e9d5ff" }}>
                            {items.map((item, i) => (
                              <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" mb={0.3}>
                                <Box>
                                  <Typography fontSize="0.82rem" fontWeight={600}>{item.name}</Typography>
                                  <Typography fontSize="0.72rem" color="text.secondary">Ordered: {item.qty} · Missing: {item.refundQty}</Typography>
                                </Box>
                                <Typography fontSize="0.82rem" fontWeight={700} color="#7c3aed">${parseFloat(item.refundAmount).toFixed(2)}</Typography>
                              </Stack>
                            ))}
                          </Box>
                        )}
                        {(() => {
                          // evidenceUrls may arrive as a JSON-encoded string,
                          // an array of relative paths, or an array of full
                          // URLs. Normalise to absolute URLs and drop empties.
                          const raw = ticket.evidenceUrls;
                          let list = [];
                          if (Array.isArray(raw)) list = raw;
                          else if (typeof raw === "string") { try { list = JSON.parse(raw); } catch { list = []; } }
                          const urls = list
                            .filter(u => typeof u === "string" && u.trim())
                            .map(u => u.startsWith("http") ? u : `${process.env.REACT_APP_SERVER_URL || ""}${u.startsWith("/") ? "" : "/"}${u}`);
                          if (urls.length === 0) return null;
                          return (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={1}>
                              {urls.map((url, i) => (
                                <Box key={i} component="a" href={url} target="_blank" rel="noreferrer">
                                  <Box
                                    component="img"
                                    src={url}
                                    alt="evidence"
                                    onError={(e) => {
                                      // Show a visible placeholder instead of a silently broken tile
                                      e.currentTarget.style.background = "#f1f5f9";
                                      e.currentTarget.style.display = "flex";
                                      e.currentTarget.alt = "image unavailable";
                                    }}
                                    sx={{
                                      width: 70, height: 70, objectFit: "cover", borderRadius: "8px",
                                      border: "1px solid #e9d5ff", cursor: "pointer",
                                      fontSize: "0.62rem", color: "#94a3b8",
                                      "&:hover": { opacity: 0.8 },
                                    }}
                                  />
                                </Box>
                              ))}
                            </Stack>
                          );
                        })()}
                      </Box>
                    );
                  })()}

                  {/* Messages */}
                  <Stack spacing={1.5}>
                    {messages.map((m) => {
                      const cfg = SENDER_CONFIG[m.senderType] || SENDER_CONFIG.ADMIN;
                      const { Icon } = cfg;
                      const displayName = m.senderName || (m.senderType === 'CUSTOMER' ? 'You' : m.senderType === 'ADMIN' ? 'RushMyFood' : 'Restaurant');
                      const urls = m.evidenceUrls
                        ? (Array.isArray(m.evidenceUrls) ? m.evidenceUrls : (() => { try { return JSON.parse(m.evidenceUrls); } catch { return []; } })())
                        : [];
                      return (
                        <Box key={m.id} sx={{ p: 1.5, borderRadius: "10px", bgcolor: cfg.bg, border: `1px solid ${cfg.border}` }}>
                          <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
                            <Icon sx={{ fontSize: 15, color: cfg.color }} />
                            <Typography fontSize="0.72rem" fontWeight={700} color={cfg.color}>{displayName}</Typography>
                            {m.issueType && (
                              <Chip label={m.issueType.replace(/_/g, " ")} size="small"
                                sx={{ fontSize: "0.6rem", height: 16, bgcolor: "#ede9fe", color: "#7c3aed" }} />
                            )}
                            <Typography fontSize="0.7rem" color="text.secondary" sx={{ ml: "auto !important" }}>{fmt(m.createdAt)}</Typography>
                          </Stack>
                          <Typography fontSize="0.85rem" sx={{ whiteSpace: "pre-line", color: "#374151" }}>{m.message}</Typography>
                          {urls.length > 0 && (
                            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                              {urls.map((url, i) => (
                                <Box key={i} component="a" href={url} target="_blank" rel="noreferrer">
                                  <Box component="img" src={url}
                                    sx={{ width: 64, height: 64, objectFit: "cover", borderRadius: "8px", border: `1px solid ${cfg.border}`, cursor: "pointer", "&:hover": { opacity: 0.8 } }} />
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      );
                    })}
                    <div ref={threadEndRef} />
                  </Stack>

                  {/* Customer reply input */}
                  {!isResolved && !isDenied && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e2e8f0" }}>
                      {/* Attached image previews */}
                      {replyImages.length > 0 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
                          {replyImages.map((img, i) => (
                            <Box key={i} sx={{ position: "relative" }}>
                              <Box component="img" src={img.preview}
                                sx={{ width: 60, height: 60, objectFit: "cover", borderRadius: "8px", border: "1px solid #e9d5ff" }} />
                              <IconButton size="small"
                                onClick={() => setReplyImages((prev) => prev.filter((_, idx) => idx !== i))}
                                sx={{ position: "absolute", top: -6, right: -6, bgcolor: "#fff", border: "1px solid #e2e8f0", p: 0.2, "&:hover": { bgcolor: "#fee2e2" } }}>
                                <CloseIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Box>
                          ))}
                        </Stack>
                      )}
                      <Stack direction="row" spacing={1} alignItems="flex-end">
                        <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={handleImageAttach} />
                        <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                          sx={{ color: "#7c3aed", border: "1px solid #e9d5ff", borderRadius: "8px", p: 0.8, flexShrink: 0 }}>
                          <AttachFileIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          fullWidth
                          multiline
                          maxRows={4}
                          placeholder="Type a reply…"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
                          size="small"
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.85rem" } }}
                        />
                        <IconButton
                          onClick={handleSendReply}
                          disabled={sending || uploading || (!replyText.trim() && replyImages.length === 0)}
                          sx={{ bgcolor: "#7c3aed", color: "#fff", borderRadius: "10px", p: 1, flexShrink: 0,
                            "&:hover": { bgcolor: "#6d28d9" }, "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }}>
                          {sending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SendIcon fontSize="small" />}
                        </IconButton>
                      </Stack>
                    </Box>
                  )}
                </Paper>

              {/* OUTCOME */}
              {isResolved && refund?.approvedAmount != null && (
                <Paper sx={{
                  p: 3, borderRadius: "14px",
                  background: "linear-gradient(135deg,#dcfce7,#bbf7d0)",
                  border: "1px solid #86efac",
                }}>
                  {refund.customerChoice === "ORIGINAL" ? (
                    <>
                      <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                        <CreditCardIcon sx={{ color: "#15803d", fontSize: 28 }} />
                        <Typography fontWeight={800} fontSize="1.1rem" color="success.dark">
                          {money(refund.approvedAmount)} refunded to your original payment
                        </Typography>
                      </Stack>
                      {refund.walletRefundAmount > 0 && (
                        <Typography fontSize="0.83rem" color="success.dark">
                          {money(refund.walletRefundAmount)} is back in your RMF Wallet.
                        </Typography>
                      )}
                      {refund.cardRefundAmount > 0 && (
                        <Typography fontSize="0.83rem" color="success.dark">
                          {money(refund.cardRefundAmount)} is on its way to {refund.cardLabel || "your card"}. It can take {refund.cardRefundDays || "3–8 business days"} to show up.
                        </Typography>
                      )}
                      <Box mb={2} />
                    </>
                  ) : (
                    <>
                      <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                        <AccountBalanceWalletIcon sx={{ color: "#15803d", fontSize: 28 }} />
                        <Typography fontWeight={800} fontSize="1.1rem" color="success.dark">
                          {refund.customerChoice === "CREDIT"
                            ? `${money(refund.creditTotal)} added as ${creditWhere(refund)}`
                            : `${money(refund.approvedAmount)} added to your RMF Wallet`}
                        </Typography>
                      </Stack>
                      <Typography fontSize="0.83rem" color="success.dark" mb={2}>
                        {refund.customerChoice === "CREDIT" && refund.creditScope === "RESTAURANT"
                          ? `Use it on your next order from ${refund.creditRestaurantName || "this restaurant"}.`
                          : "Use it on your next order — full or partial payment."}
                      </Typography>
                    </>
                  )}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button variant="contained" color="success" onClick={() => navigate("/wallet")}
                      sx={{ borderRadius: "10px", fontWeight: 700 }}>
                      View Wallet
                    </Button>
                    <Button variant="outlined" color="success" onClick={() => navigate("/")}
                      sx={{ borderRadius: "10px" }}>
                      Order Again
                    </Button>
                  </Stack>
                </Paper>
              )}

              {isDenied && (
                <Paper sx={{ p: 3, borderRadius: "14px", bgcolor: "#fff7f7", border: "1px solid #fecaca" }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#ef4444", flexShrink: 0 }} />
                    <Typography fontWeight={700} color="error.dark">Request Declined</Typography>
                  </Stack>

                  {/* Show whichever note exists — admin overrides restaurant */}
                  {(refund?.adminDecisionNote || refund?.restaurantDecisionNote || ticket?.resolutionNote) ? (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px", bgcolor: "#fff", borderColor: "#fecaca", mb: 2 }}>
                      <Typography fontSize="0.75rem" fontWeight={700} color="text.secondary" letterSpacing={0.8} mb={0.5}>
                        REASON
                      </Typography>
                      <Typography fontSize="0.85rem" color="text.primary">
                        {refund?.adminDecisionNote || refund?.restaurantDecisionNote || ticket?.resolutionNote}
                      </Typography>
                      <Typography fontSize="0.72rem" color="text.secondary" mt={0.5}>
                        — {refund?.adminDecision === "DENIED" ? "Support Team" : "Restaurant"}
                      </Typography>
                    </Paper>
                  ) : (
                    <Typography fontSize="0.83rem" color="text.secondary" mb={2}>
                      After reviewing your report, we were unable to issue a credit for this order.
                    </Typography>
                  )}

                  <Typography fontSize="0.8rem" color="text.secondary" mb={2}>
                    If you believe this is incorrect, please contact us.
                  </Typography>
                  <Button variant="outlined" onClick={() => navigate("/get-help")}
                    sx={{ borderRadius: "10px", borderColor: "#fca5a5", color: "error.dark" }}>
                    Contact Support
                  </Button>
                </Paper>
              )}

              {!isResolved && !isDenied && !awaitingChoice && (
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: "14px", textAlign: "center" }}>
                  <Typography fontSize="0.85rem" color="text.secondary">
                    We're reviewing your report. You'll be notified when it's resolved.
                  </Typography>
                  <Typography fontSize="0.8rem" color="text.secondary" mt={0.5}>
                    Expected response within 24 hours.
                  </Typography>
                </Paper>
              )}
            </Stack>
          )}
        </Container>
      </Box>
    </>
  );
}
